"use client";

import { useQuery } from "@tanstack/react-query";
import { useMemo } from "react";
import { useClassesEnrollmentsByStudentIdsGet } from "@/features/classes/hooks";
import { useStudioFeatureSettings } from "@/features/dashboard/hooks/useStudioFeatureSettings";
import {
  attendanceLogClassId,
  attendanceLogStudentId,
  buildAnnualMakeupUsageByStudent,
  buildMakeupByAttendanceLogId,
  classIdsThatDoNotOfferMakeup,
  formatMakeupsAnnualPeriodRangeLabel,
  isAttendanceLogMakeupEligible,
  isMakeupsNoAnnualLimit,
  isStudentAtAnnualMakeupLimit,
  makeupAnnualPeriodDateRange,
  makeupDaysRemainingInWindow,
  makeupEligibilityDateRange,
} from "@/features/makeups/shared";
import { useUserStudentsGet } from "@/features/profile-settings/hooks";
import { useAccessToken, useClassesGetByIds } from "@/hooks";
import { useAuth } from "@/lib/contexts";
import { fetchAttendanceLogsForStudents } from "@/lib/services/attendanceService";
import {
  MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
  toMakeupMemberId,
} from "@/lib/services/makeupService";
import type { AppUser } from "@/lib/types/next-auth";
import type {
  GetAttendanceLogResponse,
  GetClassResponse,
  GetEnrollmentResponse,
  GetMakeupResponse,
  GetStudentResponse,
} from "@/openapi/generated";
import { eligibleMakeupAttendanceQueryKey } from "./makeup-sessions-query-key";
import {
  MEMBER_MAKEUP_SESSIONS_STALE_TIME_MS,
  useMakeupSessionsSearch,
} from "./useMakeupSessionsSearch";
import { useMakeupsEligibilitySettings } from "./useMakeupsEligibilitySettings";

export type EligibleMissedSession = {
  log: GetAttendanceLogResponse;
  studentId: number;
  studentName: string;
  className: string;
  daysRemaining: number | null;
  annualRequestsUsed: number;
  annualRequestLimit: number | null;
  isAnnualLimitReached: boolean;
};

function resolveClassNameForLog(
  log: GetAttendanceLogResponse,
  studentId: number,
  classesMap: Map<number, GetClassResponse>,
  enrollmentsMap: Record<number, GetEnrollmentResponse[]>,
): string {
  const classId = attendanceLogClassId(log);
  if (classId != null) {
    const fromMap = classesMap.get(classId)?.name;
    if (fromMap) return fromMap;
    const fromEnrollment = (enrollmentsMap[studentId] ?? []).find(
      (e) => Number(e._class?.classId) === classId,
    )?._class?.name;
    if (fromEnrollment) return fromEnrollment;
    return `Class #${classId}`;
  }
  return "Class";
}

function studentNameFromList(
  studentId: number,
  students: GetStudentResponse[],
): string {
  const student = students.find((s) => Number(s.studentId) === studentId);
  if (!student) return `Student #${studentId}`;
  return (
    `${student.firstName} ${student.lastName}`.trim() || `Student #${studentId}`
  );
}

function logDateValue(d: Date | string): number {
  return d instanceof Date ? d.getTime() : new Date(d).getTime();
}

export function useEligibleMakeupSessions({
  enabled = true,
  /** When provided, skips makeup search and uses these sessions for eligibility. */
  makeupSessions,
}: {
  enabled?: boolean;
  makeupSessions?: GetMakeupResponse[];
} = {}) {
  const accessToken = useAccessToken();
  const user = useAuth() as AppUser | null;
  const userId = user?.id ?? 0;
  const memberId = toMakeupMemberId(userId);
  const usesExternalMakeupSessions = makeupSessions !== undefined;
  const {
    expiryDays,
    annualRequestLimit,
    annualPeriod,
    isPending: isSettingsPending,
  } = useMakeupsEligibilitySettings({
    enabled,
  });
  const { appointmentsEnabled } = useStudioFeatureSettings();

  const referenceDate = useMemo(() => new Date(), []);
  const annualPeriodLabel = useMemo(
    () => formatMakeupsAnnualPeriodRangeLabel(annualPeriod, referenceDate),
    [annualPeriod, referenceDate],
  );
  const annualPeriodRange = useMemo(
    () => makeupAnnualPeriodDateRange(annualPeriod, referenceDate),
    [annualPeriod, referenceDate],
  );

  const { data: students = [], isPending: isStudentsPending } =
    useUserStudentsGet({
      userId,
      enabled: enabled && userId > 0,
      refetchOnMount: false,
    });

  const studentIds = useMemo(
    () =>
      students
        .map((s) => Number(s.studentId))
        .filter((id) => Number.isFinite(id) && id > 0),
    [students],
  );

  const studentIdsKey = useMemo(
    () => [...new Set(studentIds)].sort((a, b) => a - b).join(","),
    [studentIds],
  );

  const { data: enrollmentsData, isPending: isEnrollmentsPending } =
    useClassesEnrollmentsByStudentIdsGet(studentIds);

  const enrollmentsMap = useMemo(() => {
    const map: Record<number, GetEnrollmentResponse[]> = {};
    for (const enrollment of enrollmentsData?.items ?? []) {
      const sid = enrollment.student?.studentId;
      if (sid == null) continue;
      const id = Number(sid);
      if (!map[id]) map[id] = [];
      map[id].push(enrollment);
    }
    return map;
  }, [enrollmentsData?.items]);

  const uniqueClassIds = useMemo(() => {
    const classIds = (enrollmentsData?.items ?? [])
      .filter((e) => e._class?.classId != null)
      .map((e) => Number(e._class!.classId));
    return Array.from(new Set(classIds)).sort((a, b) => a - b);
  }, [enrollmentsData?.items]);

  const { classesMap, isPending: isClassesPending } = useClassesGetByIds({
    classIds: uniqueClassIds,
    enabled: enabled && uniqueClassIds.length > 0 && !!accessToken,
  });

  const {
    data: makeupSessionsData,
    isPending: isMakeupsPending,
    refetch: refetchMakeups,
    isFetching: isFetchingMakeups,
  } = useMakeupSessionsSearch({
    pageSize: MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
    filters: { memberId },
    enabled:
      !usesExternalMakeupSessions &&
      enabled &&
      !!accessToken &&
      memberId != null &&
      studentIds.length > 0,
    staleTime: MEMBER_MAKEUP_SESSIONS_STALE_TIME_MS,
    refetchOnMount: false,
  });

  const {
    data: annualMakeupSessionsData,
    isPending: isAnnualMakeupsPending,
    isFetching: isFetchingAnnualMakeups,
  } = useMakeupSessionsSearch({
    pageSize: MEMBER_MAKEUP_SESSIONS_PAGE_SIZE,
    filters: {
      memberId,
      fromDate: annualPeriodRange.fromDate,
      toDate: annualPeriodRange.toDate,
    },
    enabled:
      enabled &&
      !!accessToken &&
      memberId != null &&
      studentIds.length > 0 &&
      !isSettingsPending,
    staleTime: MEMBER_MAKEUP_SESSIONS_STALE_TIME_MS,
    refetchOnMount: false,
  });

  const resolvedMakeupSessions = usesExternalMakeupSessions
    ? makeupSessions
    : (makeupSessionsData?.items ?? []);

  const annualMakeupSessions = annualMakeupSessionsData?.items ?? [];

  const annualUsageByStudent = useMemo(
    () =>
      buildAnnualMakeupUsageByStudent(
        annualMakeupSessions,
        studentIds,
        annualPeriod,
        referenceDate,
      ),
    [annualMakeupSessions, studentIds, annualPeriod, referenceDate],
  );

  const makeupByAttendanceLogId = useMemo(
    () => buildMakeupByAttendanceLogId(resolvedMakeupSessions),
    [resolvedMakeupSessions],
  );

  const {
    data: attendanceLogs = [],
    isPending: isAttendancePending,
    isError: isAttendanceError,
    refetch: refetchAttendance,
    isFetching: isFetchingAttendance,
  } = useQuery({
    queryKey: eligibleMakeupAttendanceQueryKey(studentIdsKey, expiryDays),
    queryFn: async () => {
      if (!accessToken || studentIds.length === 0) return [];
      const { fromDate, toDate } = makeupEligibilityDateRange(expiryDays);
      return fetchAttendanceLogsForStudents(
        accessToken,
        studentIds,
        fromDate,
        toDate,
      );
    },
    enabled:
      enabled && !!accessToken && studentIds.length > 0 && !isSettingsPending,
    staleTime: 60_000,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    retry: false,
  });

  const classesThatDoNotOfferMakeup = useMemo(
    () => classIdsThatDoNotOfferMakeup(classesMap.values()),
    [classesMap],
  );

  const items = useMemo((): EligibleMissedSession[] => {
    const eligible: EligibleMissedSession[] = [];

    for (const log of attendanceLogs) {
      const studentId = attendanceLogStudentId(log);
      if (studentId == null) continue;

      // The member attendance API keeps absences while the student is enrolled
      // in any class, including a class they already left. Do not drop those here.
      if (
        !isAttendanceLogMakeupEligible(
          log,
          makeupByAttendanceLogId,
          expiryDays,
          classesThatDoNotOfferMakeup,
          { appointmentsEnabled },
        )
      ) {
        continue;
      }

      const annualRequestsUsed = annualUsageByStudent.get(studentId) ?? 0;
      const isAnnualLimitReached = isStudentAtAnnualMakeupLimit(
        annualRequestsUsed,
        annualRequestLimit,
      );
      const annualLimitValue = isMakeupsNoAnnualLimit(annualRequestLimit)
        ? null
        : annualRequestLimit;

      eligible.push({
        log,
        studentId,
        studentName: studentNameFromList(studentId, students),
        className: resolveClassNameForLog(
          log,
          studentId,
          classesMap,
          enrollmentsMap,
        ),
        daysRemaining: makeupDaysRemainingInWindow(log, expiryDays),
        annualRequestsUsed,
        annualRequestLimit: annualLimitValue,
        isAnnualLimitReached,
      });
    }

    eligible.sort(
      (a, b) => logDateValue(b.log.date) - logDateValue(a.log.date),
    );
    return eligible;
  }, [
    attendanceLogs,
    enrollmentsMap,
    makeupByAttendanceLogId,
    students,
    classesMap,
    classesThatDoNotOfferMakeup,
    appointmentsEnabled,
    annualRequestLimit,
    annualUsageByStudent,
    expiryDays,
  ]);

  const isPending =
    isSettingsPending ||
    isStudentsPending ||
    isEnrollmentsPending ||
    isClassesPending ||
    (!usesExternalMakeupSessions && isMakeupsPending) ||
    isAnnualMakeupsPending ||
    isAttendancePending;

  const refetch = () => {
    void refetchAttendance();
    if (!usesExternalMakeupSessions) {
      void refetchMakeups();
    }
  };

  return {
    items,
    makeupSessions: resolvedMakeupSessions,
    isPending,
    isAttendanceError,
    refetch,
    isFetching:
      isFetchingAttendance ||
      isFetchingAnnualMakeups ||
      (!usesExternalMakeupSessions && isFetchingMakeups),
    annualRequestLimit,
    annualPeriod,
    annualPeriodLabel,
  };
}
